import * as React from 'react'
import { cn } from '../../lib/utils'
import {
 injectSequenceIntoBodyRows,
 injectSequenceIntoHeaderRows,
 setTableRowRef,
 TableOptionsContext,
 useTableSequencingEnabled,
} from './table-sequence'

const Table = React.forwardRef<
 HTMLTableElement,
 React.HTMLAttributes<HTMLTableElement> & {
 compact?: boolean
 /** Show # column (default: true unless compact) */
 sequenced?: boolean
 }
>(({ className, compact, sequenced, ...props }, ref) => {
 const enabled = sequenced ?? !compact

 return (
 <TableOptionsContext.Provider value={{ sequenced: enabled, compact: !!compact }}>
 <div
 className={cn(
 'erp-data-table-shell relative w-full overflow-auto',
 compact && 'erp-data-table-compact',
 enabled && 'erp-data-table-sequenced',
 )}
 >
 <table
 ref={ref}
 className={cn('w-full caption-bottom text-sm', className)}
 {...props}
 />
 </div>
 </TableOptionsContext.Provider>
 )
})
Table.displayName = 'Table'

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => {
  const sequenced = useTableSequencingEnabled()

  return (
    <thead
      ref={ref}
      className={cn(className)}
      {...props}
    >
      {sequenced ? injectSequenceIntoHeaderRows(children) : children}
    </thead>
  )
})
TableHeader.displayName = 'TableHeader'

const TableBody = React.forwardRef<
 HTMLTableSectionElement,
 React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => {
 const sequenced = useTableSequencingEnabled()

 return (
 <tbody
 ref={ref}
 className={cn('[&_tr:last-child]:border-0', className)}
 {...props}
 >
 {sequenced ? injectSequenceIntoBodyRows(children) : children}
 </tbody>
 )
})
TableBody.displayName = 'TableBody'

const TableFooter = React.forwardRef<
 HTMLTableSectionElement,
 React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
 <tfoot
 ref={ref}
 className={cn(
 'border-t border-border-subtle bg-muted/50 font-medium [&>tr]:last:border-b-0',
 className,
 )}
 {...props}
 />
))
TableFooter.displayName = 'TableFooter'

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-border-subtle/50 transition-colors duration-200 ease-out data-[state=selected]:bg-primary-soft/40',
      className,
    )}
    {...props}
  />
))
TableRow.displayName = 'TableRow'
setTableRowRef(TableRow)

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'h-12 px-4 text-start align-middle text-xs font-bold tracking-tight text-muted-foreground [&:has([role=checkbox])]:pe-0',
      className,
    )}
    {...props}
  />
))
TableHead.displayName = 'TableHead'

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'px-4 py-3.5 align-middle text-[13px] leading-relaxed text-foreground [&:has([role=checkbox])]:pe-0',
      className,
    )}
    {...props}
  />
))
TableCell.displayName = 'TableCell'

const TableCaption = React.forwardRef<
 HTMLTableCaptionElement,
 React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
 <caption
 ref={ref}
 className={cn('mt-4 text-sm text-muted-foreground', className)}
 {...props}
 />
))
TableCaption.displayName = 'TableCaption'

export {
 Table,
 TableHeader,
 TableBody,
 TableFooter,
 TableHead,
 TableRow,
 TableCell,
 TableCaption,
}
